'use client'

import type { BarProps } from 'recharts/types/cartesian/Bar'

import { useMemo } from 'react'
import {
  Bar,
  BarChart,
  Cell,
  Customized,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import Icon from '@/components/atomic/icon'
import { IconName } from '@/components/atomic/icon/types'
import { formatPercentage } from '@/utils/presentation/formatting'

import {
  NoDataText,
  TooltipColorIndicator,
  TooltipContainer,
  TooltipItemContainer,
  TooltipItemText,
  TooltipLabel,
  YAxisTickText,
} from './styles'

const DEFAULT_BAR_SIZE = 24

interface BarConfig {
  key: string
  fill: string
  stackId?: string
  radius?: [number, number, number, number]
  violationColor?: string
  // biome-ignore lint/suspicious/noExplicitAny: Bulk suppression as part of biome migration
  isHighlighted?: boolean | ((entry: any) => boolean)
}

interface HorizontalTargetBarChartProps<T> {
  data: T[]
  yAxisKey: string
  bars: BarConfig[]
  tooltipFormatter?: (dataPoint: T) => React.ReactNode
  minValue?: number
  maxValue?: number
  targetValue?: number
  yAxisLabel?: string
  tooltipLabelPrefix?: string
  barSize?: number
  valueFormatter?: (value: number) => string
}

interface TooltipPayloadItem {
  name: string
  value: number
  fill: string
}

interface CustomTooltipProps {
  active?: boolean
  payload?: TooltipPayloadItem[]
  label?: string
  labelPrefix?: string
}

interface CustomBarProps {
  x?: number
  y?: number
  width?: number
  height?: number
  value?: number
  fill?: string
  minValue?: number
  maxValue?: number
  radius?: [number, number, number, number]
  violationColor?: string
}

const CustomBar = ({
  x = 0,
  y = 0,
  width = 0,
  height = 0,
  value = 0,
  fill,
  minValue,
  maxValue,
  radius,
  violationColor,
}: CustomBarProps) => {
  const isViolation =
    minValue !== undefined &&
    maxValue !== undefined &&
    (value < minValue || value > maxValue)

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill={isViolation ? violationColor || 'var(--destructive)' : fill}
        rx={radius?.[0] || 0}
        ry={radius?.[0] || 0}
      />
      {isViolation && (
        <foreignObject
          x={x + width + 4}
          y={y + height / 2 - 8}
          width={16}
          height={16}
        >
          <div className="h-4 w-4">
            <Icon
              iconName={IconName.ExclamationTriangleIcon}
              className="h-4 w-4 text-moment-red-700"
            />
          </div>
        </foreignObject>
      )}
    </g>
  )
}

const BarChartNoData = () => {
  return <NoDataText>No data available</NoDataText>
}

// biome-ignore lint/suspicious/noExplicitAny: Bulk suppression as part of biome migration
const CustomYAxisTick = ({ x, y, payload }: any) => {
  return (
    <g transform={`translate(${x},${y})`}>
      <YAxisTickText x={-8} y={0} textAnchor="end" dominantBaseline="middle">
        {payload.value}
      </YAxisTickText>
    </g>
  )
}

const CustomTooltip = ({
  active,
  payload,
  label,
  labelPrefix,
}: CustomTooltipProps) => {
  if (!active || !payload?.length) {
    return null
  }

  return (
    <TooltipContainer>
      <TooltipLabel>{`${labelPrefix} ${label}`}</TooltipLabel>
      {payload.map((entry, index) => (
        <TooltipItemContainer key={index}>
          <TooltipColorIndicator style={{ backgroundColor: entry.fill }} />
          <TooltipItemText>
            {`${entry.name}: ${formatPercentage(entry.value, { numDigits: 2 })}`}
          </TooltipItemText>
        </TooltipItemContainer>
      ))}
    </TooltipContainer>
  )
}

export function HorizontalTargetBarChart<T>({
  data,
  yAxisKey,
  bars,
  tooltipFormatter,
  minValue,
  maxValue,
  targetValue,
  yAxisLabel = '',
  tooltipLabelPrefix = 'Sector',
  barSize = DEFAULT_BAR_SIZE,
  valueFormatter = (value) => formatPercentage(value, { numDigits: 2 }),
}: HorizontalTargetBarChartProps<T>) {
  const hasNoData = useMemo(() => data.length === 0, [data])

  const formattedMaxValue = useMemo(
    () => maxValue && valueFormatter(maxValue),
    [maxValue, valueFormatter],
  )

  const formattedMinValue = useMemo(
    () => minValue && valueFormatter(minValue),
    [minValue, valueFormatter],
  )

  const formattedTargetValue = useMemo(
    () => targetValue && valueFormatter(targetValue),
    [targetValue, valueFormatter],
  )

  const tooltipContent = useMemo(
    () =>
      tooltipFormatter
        ? // biome-ignore lint/suspicious/noExplicitAny: Bulk suppression as part of biome migration
          (props: any) => {
            if (!props.payload || props.payload.length === 0) return null
            return tooltipFormatter(props.payload[0].payload as T)
          }
        : // biome-ignore lint/suspicious/noExplicitAny: Bulk suppression as part of biome migration
          (props: any) => (
            <CustomTooltip {...props} labelPrefix={tooltipLabelPrefix} />
          ),
    [tooltipFormatter, tooltipLabelPrefix],
  )

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={data}
        layout="vertical"
        margin={{ top: 40, right: 20, left: 40, bottom: 5 }}
        maxBarSize={barSize}
      >
        {hasNoData ? (
          <Customized component={<BarChartNoData />} />
        ) : (
          <>
            <YAxis
              dataKey={yAxisKey}
              type="category"
              interval={0}
              axisLine={false}
              tickLine={false}
              tick={<CustomYAxisTick />}
              width={120}
              label={{
                value: yAxisLabel,
                position: 'left',
                offset: -100,
                fontSize: 9,
              }}
            />
            <XAxis
              type="number"
              axisLine={false}
              tickLine={false}
              tick={false}
              domain={[
                0,
                maxValue ? Math.max(maxValue + 1, maxValue * 1.5) : 'auto',
              ]}
              ticks={[minValue, targetValue, maxValue].filter(
                (v): v is number => typeof v === 'number',
              )}
            />
            <Tooltip
              content={tooltipContent}
              cursor={false}
              animationDuration={50}
            />

            {/* Background colors */}
            {minValue !== undefined && maxValue !== undefined && (
              <ReferenceArea
                x1={minValue}
                x2={maxValue}
                fill="var(--reference-area-background)"
                fillOpacity={0.2}
              />
            )}

            {/* Reference lines */}
            {maxValue !== undefined && (
              <ReferenceLine
                x={maxValue}
                stroke="var(--reference-line-color)"
                strokeDasharray="3 3"
                label={{
                  value: `Max ${formattedMaxValue}`,
                  position: 'top',
                  fill: 'var(--reference-line-color)',
                  fontSize: 9,
                  offset: 10,
                }}
              />
            )}

            {targetValue !== undefined && (
              <ReferenceLine
                x={targetValue}
                stroke="var(--reference-line-target-color)"
                strokeDasharray="3 3"
                label={{
                  value: `Target ${formattedTargetValue}`,
                  position: 'top',
                  fill: 'var(--reference-line-target-color)',
                  offset: 10,
                  fontSize: 9,
                }}
              />
            )}

            {minValue !== undefined && (
              <ReferenceLine
                x={minValue}
                stroke="var(--reference-line-color)"
                strokeDasharray="3 3"
                label={{
                  value: `Min ${formattedMinValue}`,
                  position: 'top',
                  fill: 'var(--reference-line-color)',
                  fontSize: 9,
                  offset: 10,
                }}
              />
            )}

            {bars.map((bar) => (
              <Bar
                key={bar.key}
                dataKey={bar.key}
                stackId={bar.stackId}
                radius={bar.radius}
                shape={(props: BarProps & { fill?: string }) => (
                  <CustomBar
                    {...props}
                    fill={bar.isHighlighted ? bar.fill : 'var(--slate-600)'}
                    minValue={minValue}
                    maxValue={maxValue}
                    radius={bar.radius}
                    violationColor={bar.violationColor}
                  />
                )}
              >
                {data.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={bar.fill} />
                ))}
              </Bar>
            ))}
          </>
        )}
      </BarChart>
    </ResponsiveContainer>
  )
}
